import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { usePremium } from '@/hooks/usePremium';
import { hasFeatureInGroup } from '@/lib/premium';
import { supabase } from '@/lib/supabase';
import { fetchAllRows } from '@/lib/fetchAllRows';
import { fetchExchangeRates } from '@/api/currencies';
import {
  computeBudgetStatus,
  getPeriodBoundaries,
  isGroupBudgetEnabled,
} from '@/lib/budget';
import type { BudgetStatus, BudgetExpenseRow } from '@/lib/budget';
import type { GroupWithMembers } from '@/api/groups';

type ExpenseRowWithGroup = BudgetExpenseRow & { group_id: string };

/**
 * Batch-fetches budget status for every budget-enabled group on the dashboard
 * where the viewer has premium access (directly or via a premium group member).
 * Skips FREE groups and groups without a budget enabled, so a user with no
 * accessible budgets never triggers the query.
 */
export function useDashboardBudgets(groups: GroupWithMembers[] | undefined) {
  const { user } = useAuth();
  const { tier } = usePremium();

  const budgetEnabledGroups = useMemo(
    () =>
      (groups ?? []).filter(
        g => isGroupBudgetEnabled(g) && hasFeatureInGroup(tier, 'group_budgets', g.members)
      ),
    [groups, tier]
  );

  const groupIdsKey = useMemo(
    () => budgetEnabledGroups.map(g => g.id).sort(),
    [budgetEnabledGroups]
  );

  return useQuery<Map<string, BudgetStatus>>({
    queryKey: ['dashboard-budgets', user?.id, groupIdsKey],
    queryFn: async () => {
      if (budgetEnabledGroups.length === 0) return new Map();

      // Find the earliest period start across all budget-enabled groups —
      // one server-side date filter covers the union of periods.
      const earliestStart = budgetEnabledGroups.reduce<Date>((earliest, g) => {
        const { start } = getPeriodBoundaries(
          g.budget_period as 'monthly' | 'total',
          g.created_at,
          g.budget_start_day
        );
        return start < earliest ? start : earliest;
      }, new Date());

      const [expenseRows, rates] = await Promise.all([
        fetchAllRows(
          supabase
            .from('expenses')
            .select(
              'group_id, amount_minor_src, amount_minor_usd, currency_code_src, expense_date'
            )
            .in('group_id', budgetEnabledGroups.map(g => g.id))
            .eq('_deleted', false)
            .gte('expense_date', earliestStart.toISOString().slice(0, 10)),
        ),
        fetchExchangeRates(),
      ]);

      const expensesByGroup = new Map<string, BudgetExpenseRow[]>();
      for (const row of expenseRows as ExpenseRowWithGroup[]) {
        const arr = expensesByGroup.get(row.group_id) ?? [];
        arr.push(row);
        expensesByGroup.set(row.group_id, arr);
      }

      const result = new Map<string, BudgetStatus>();
      for (const group of budgetEnabledGroups) {
        const status = computeBudgetStatus(
          group,
          expensesByGroup.get(group.id) ?? [],
          rates
        );
        if (status) result.set(group.id, status);
      }
      return result;
    },
    enabled: !!user && budgetEnabledGroups.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
